import { marked } from 'marked';

import { IUpdateContentBlock } from '../../../services/shared/update-announcements/update-announcement.interface';
import { UpdateAnnouncementFeedHelper } from '../../../services/shared/update-announcements/update-announcement-feed.helper';
import { IRenderedUpdateContentBlock, IUpdateLinkDestination, TypeUpdateAppUrlFormatter, TypeUpdateLinkResolver } from './update-content.interface';


export class UpdateContentHelper {

	/**------------------------------------------------------
	 * Prepare Content
	 */
	static renderBlocks(blocks: IUpdateContentBlock[], baseUrl: string, prepareAppUrl: TypeUpdateAppUrlFormatter): IRenderedUpdateContentBlock[] {
		const resolveLink: TypeUpdateLinkResolver = (url: string) => this.resolveLink(url, baseUrl, prepareAppUrl);

		return (blocks || []).map((block: IUpdateContentBlock) => ({
			...block,
			html: block.markdown ? this.renderMarkdown(block.markdown, resolveLink) : '',
			link: resolveLink(block.url || ''),
			actions: block.actions?.map((action) => ({ ...action, link: resolveLink(action.url) }))
		}));
	}

	static renderMarkdown(markdown: string, resolveLink: TypeUpdateLinkResolver = (url: string) => ({ href: url, target: url.startsWith('/') ? '_self' : '_blank' })): string {
		const renderer: marked.Renderer = new marked.Renderer();

		// Existing feeds may use Markdown, but raw HTML and unsafe URLs are never rendered.
		renderer.html = () => '';
		renderer.link = (href: string, title: string, label: string) => {
			const url: string | null = UpdateAnnouncementFeedHelper.readUrl(href, true);
			if (!url) return label;

			const destination: IUpdateLinkDestination = resolveLink(url);
			return `<a href="${this.escapeAttribute(destination.href)}" title="${this.escapeAttribute(title || '')}" target="${destination.target}" rel="noopener noreferrer">${label}</a>`;
		};
		renderer.image = (href: string, title: string, alt: string) => {
			const url: string | null = UpdateAnnouncementFeedHelper.readUrl(href, true);
			return url ? `<img src="${this.escapeAttribute(url)}" alt="${this.escapeAttribute(alt)}" title="${this.escapeAttribute(title || '')}" loading="lazy">` : '';
		};

		return marked.parse(markdown, { renderer, gfm: true, breaks: false, headerIds: false, mangle: false }) as string;
	}


	/**------------------------------------------------------
	 * Link Helpers
	 */
	static getAppRoute(href: string | null, baseUrl: string): string | null {
		if (!href) return null;
		if (href.startsWith('/') && !href.startsWith('//')) return href;
		if (href.startsWith('#/')) return href.slice(1);

		try {
			const url: URL = new URL(href, baseUrl);
			if (url.origin !== new URL(baseUrl).origin) return null;
			return url.hash.startsWith('#/') ? url.hash.slice(1) : `${url.pathname}${url.search}${url.hash}`;
		} catch {
			return null;
		}
	}

	private static resolveLink(url: string, baseUrl: string, prepareAppUrl: TypeUpdateAppUrlFormatter): IUpdateLinkDestination {
		const route: string | null = this.getAppRoute(url, baseUrl);
		return {
			href: route ? prepareAppUrl(route) : url || '',
			target: route ? '_self' : '_blank'
		};
	}

	private static escapeAttribute(value: string): string {
		return value.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
	}
}
